/// <amd-module name="Dashboards/DashboardPanel/DashboardPanel" />
import { Dashboard, DashboardScope } from "Dashboards/Common/Contracts/Dashboard";
import { Constants, DashboardType, FormFields, Group, Project, Team, TeamProjectItem } from "Dashboards/DashboardPanel/Common";
import * as Resources from "Dashboards/DashboardPanel/Resources";
import * as React from "react";
import { announce } from "VSS/Core/Util/Accessibility";
import { format, localeComparer } from "VSS/Core/Util/String";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureEnabled } from "VSS/Platform/Feature";
import { IPageData, IVssPageService } from "VSS/Platform/Page";
import { IButtonProps } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { IVssComponentContext, VssComponent, WrappedComponent } from "VSS/Platform/Layout";
import { IdentityPickerDropdown, IIdentity, IPeoplePickerProvider } from "VSSUI/IdentityPicker";
import { IListBoxItem, ListBoxItemType } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { TextField, TextFieldWidth } from "VSSUI/TextField";
import { getSafeId } from "VSSUI/Util";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";
interface DashboardPanelProps {
    allTeams: Team[];
    dashboard?: Dashboard;
    dashboardTeam?: Team;
    dashboardProject: Project;
    dashboardOwner?: IIdentity;
    description?: string;
    disableOwnerField: boolean;
    hidePanel: boolean;
    onDismiss: () => void;
    onSubmit: (fields: FormFields, selectedDashboardType: string) => Promise<void>;
    showDashboardTypeSelector?: boolean;
    showDashboardProjectTeamSelector?: boolean;
    showOwnerField?: boolean;
    showQueryFolderSelector?: boolean;
    submitButtonLabel: string;
    title: string | ((panelName: string) => string);
    userTeams: Team[];
    projects?: Project[];
    projectTeamMap?: { [id: string]: TeamProjectItem };
    onProjectSelectLoadTeam?: (project: Project) => void;
}

interface FormErrors {
    name: string | null;
    projectId: string | null;
    teamId: string | null;
    description: string | null;
    owner: string | null;
}

interface DashboardPanelState {
    fields: FormFields;
    errors: FormErrors;
    projectItems: IListBoxItem[];
    teamItems: IListBoxItem[];
    projectsLoading: boolean;
    teamsLoading: boolean;
    selectedDashboardType: string;
    submitting: boolean;
    submissionError: string | null;
    disableQueryCheckboxfield: boolean;
}
function validate(fields: FormFields, showOwnerField?: boolean): FormErrors {
    const errors = {
        name: null,
        description: null,
        owner: null
    } as FormErrors;

    if (!fields.name || !fields.name.trim()) {
        errors.name = Resources.ValueRequired;
    } else if (fields.name.trim().length >= Constants.maxNameLength) {
        errors.name = format(Resources.NameFieldMaxLengthError, Constants.maxNameLength);
    }

    if (fields.description && fields.description.trim().length >= Constants.maxDescriptionLength) {
        errors.description = format(Resources.DescriptionFieldMaxLengthError, Constants.maxDescriptionLength);
    }

    if (showOwnerField && !fields.owner) {
        errors.owner = Resources.OwnerNotFoundError;
    }
    if (fields.copyQueriesFlag) {
        if (fields.queryFolderPath && fields.queryFolderPath.trim().length >= Constants.maxDescriptionLength) {
            errors.owner = Resources.QueryFolderDropdownFieldLabelError;
        }
    }
    return errors;
}

export class DashboardPanel extends VssComponent<DashboardPanelProps, DashboardPanelState> {
    private static readonly peoplePickerMinResultsToDisplay = 5;
    private static readonly peoplePickerMaxResultsToDisplay = 20;

    public static defaultProps = {
        disableOwnerField: false,
        hidePanel: false,
        showDashboardTypeSelector: false,
        showOwnerField: false,
        showQueryFolderSelector: false
    };

    private pickerProvider: IPeoplePickerProvider;
    private teamSelection = new DropdownSelection();
    private projectSelection = new DropdownSelection();

    constructor(props: DashboardPanelProps, context: IVssComponentContext) {
        super(props, context);

        if (props.showOwnerField && props.dashboard != null) {
            this.initializeOwnerIdentityPickerProvider();
        }

        this.state = {
            fields: this.getInitialFields(),
            errors: {
                name: null,
                description: null,
                projectId: props.dashboardProject && props.dashboardProject.id != null ? null : Resources.ValueRequired,
                teamId: props.dashboard != null ? null : Resources.ValueRequired,
                owner: props.showOwnerField && !props.dashboardOwner ? Resources.OwnerNotFoundError : null
            },
            projectItems: [],
            teamItems: [],
            projectsLoading: true,
            teamsLoading: true,
            selectedDashboardType: DashboardType.Team,
            submitting: false,
            submissionError: null,
            disableQueryCheckboxfield: false
        };
    }

    public componentDidMount() {
        if (this.props.showDashboardTypeSelector) {
            this.loadTeams();
        }

        if (this.props.showDashboardProjectTeamSelector) {
            this.loadProjectAndTeams();
        }
        if (this.props.showQueryFolderSelector) {
            this.loadQueryFolder();
        }
    }

    public componentDidUpdate(prevProps: DashboardPanelProps) {
        //Initial load for copy dashboard
        if (this.props.showDashboardProjectTeamSelector && prevProps.projects != this.props.projects) {
            this.loadProjectAndTeams();
        }
        //Subsequent loads
        else if (this.props.showDashboardProjectTeamSelector && prevProps.projectTeamMap != this.props.projectTeamMap) {
            this.loadTeamsForSpecificProject();
        }
    }

    public render(): JSX.Element | null {
        if (this.props.hidePanel) {
            return null;
        }

        return (
            <Panel
                description={this.props.description}
                footerButtonProps={this.getFooterButtonProps()}
                onDismiss={this.handleDismiss}
                titleProps={{
                    text: this.getTitleText()
                }}
            >
                <div className="dashboard-panel-content">
                    {this.renderSubmissionErrorMessage()}
                    <FormItem
                        className="dashboard-panel-form-item"
                        label={Resources.NameFieldLabel}
                        error={this.state.errors.name != null}
                        message={this.state.errors.name}
                    >
                        <TextField
                            value={this.state.fields.name}
                            placeholder={Resources.NameFieldPlaceholder}
                            width={TextFieldWidth.auto}
                            onChange={this.onNameChange}
                            required
                            disabled={this.state.submitting}
                        />
                    </FormItem>
                    <FormItem
                        label={Resources.DescriptionFieldLabel}
                        error={this.state.errors.description != null}
                        message={this.state.errors.description}
                    >
                        <TextField
                            placeholder={Resources.DescriptionFieldPlaceholder}
                            multiline
                            rows={10}
                            value={this.state.fields.description}
                            onChange={this.onDescriptionChange}
                            width={TextFieldWidth.auto}
                            disabled={this.state.submitting}
                        />
                    </FormItem>
                    <Checkbox
                        label={Resources.AutoRefreshFieldLabel}
                        checked={this.state.fields.autoRefresh}
                        onChange={this.onAutoRefreshChange}
                        disabled={this.state.submitting}
                    />

                    {this.props.showDashboardTypeSelector && this.renderDashboardTypeSelector()}
                    {this.props.showDashboardProjectTeamSelector && this.renderDashboardProjectTeamSelector()}
                    {this.props.showOwnerField && this.renderOwnerField()}
                    {this.props.showQueryFolderSelector && (
                        <Checkbox
                            label={Resources.CopyDashboardQueriesCheckBoxLabel}
                            checked={this.state.fields.copyQueriesFlag}
                            onChange={this.onCopyQueriesFlagChange}
                            disabled={this.state.disableQueryCheckboxfield || this.state.submitting}
                        />
                    )}
                    {(this.props.showQueryFolderSelector && this.state.fields.copyQueriesFlag) && this.renderQueryFolderDropdownField()}
                    {this.props.children}
                </div>
            </Panel>
        );
    }

    private initializeOwnerIdentityPickerProvider(): void {
        const pageService: IVssPageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData: IPageData | undefined = pageService.getData();

        this.pickerProvider = new PeoplePickerProvider(this.context.pageContext, {
            identityTypes: this.isTeamDashboard() ? ["group"] : ["user"],
            options: {
                MinResults: DashboardPanel.peoplePickerMinResultsToDisplay,
                MaxResults: DashboardPanel.peoplePickerMaxResultsToDisplay
            },
            operationScopes: pageData!.isHosted ? ["ims", "source"] : ["ims", "source", "ad"]
        });
    }

    private getFooterButtonProps(): IButtonProps[] {
        return [
            {
                text: this.state.submitting ? "" : this.props.submitButtonLabel,
                primary: true,
                onClick: this.handleSubmit,
                disabled: this.isFormInvalid() || this.state.submitting,
                iconProps: this.state.submitting
                    ? {
                          render: () => <Spinner size={SpinnerSize.small} />
                      }
                    : undefined
            },
            {
                text: Resources.CancelButtonLabel,
                onClick: this.handleDismiss,
                disabled: this.state.submitting
            }
        ];
    }

    private getInitialFields(): FormFields {
        if (this.props.dashboard != null) {
            // For Manage and Copy
            return {
                name: this.props.dashboard.name || "",
                description: this.props.dashboard.description || "",
                teamId: this.props.dashboardTeam ? this.props.dashboardTeam.id : undefined,
                projectId: this.props.dashboardProject ? this.props.dashboardProject.id : undefined,
                owner: this.props.dashboardOwner || undefined,
                autoRefresh: this.props.dashboard.refreshInterval === Constants.autoRefreshIntervalInMins,
                copyQueriesFlag: false,
                queryFolderPath: Resources.SharedQueries
            };
        }

        // For Create
        return {
            name: "",
            description: "",
            teamId: undefined,
            projectId: this.props.dashboardProject ? this.props.dashboardProject.id : undefined,
            owner: undefined,
            autoRefresh: false,
            copyQueriesFlag: false,
            queryFolderPath: ""
        };
    }

    private getInitialProjectId(): string {
        return this.props.dashboardProject && this.props.dashboardProject.id
            ? this.props.dashboardProject.id
            : this.props.projects && this.props.projects[0] && this.props.projects[0].id
            ? this.props.projects[0].id
            : "";
    }

    private getInitialTeamId(): string {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const teamData: { value: Team } = contributionService.getData<any>("ms.vss-dashboards-web.dashboards-team-data-provider");
        return teamData && teamData.value && teamData.value.id;
    }
    private getInitialQueryId(): string {
        return Resources.SharedQueries;
    }

    private getTeamDropdownItems(useProjectTeamMap: boolean = false, projectId: string = ""): IListBoxItem[] {
        let teamItems: IListBoxItem[];

        if (!useProjectTeamMap) {
            const unassociatedTeams = this.props.allTeams.filter((team) => {
                return !this.props.userTeams.some((userTeam) => userTeam.id === team.id);
            });
            const userTeamItems = this.getTeamItems(this.props.userTeams);
            const unassociatedTeamItems = this.getTeamItems(unassociatedTeams);
            const dividerItem = { id: "divider", type: ListBoxItemType.Divider };

            teamItems = userTeamItems;
            if (unassociatedTeamItems.length) {
                if (teamItems.length) {
                    teamItems = teamItems.concat(dividerItem);
                }

                teamItems = teamItems.concat(unassociatedTeamItems);
            }
        } else {
            teamItems =
                this.props.projectTeamMap && this.props.projectTeamMap[projectId]
                    ? this.getTeamItems(this.props.projectTeamMap[projectId].teamList)
                    : ([] as IListBoxItem[]);
        }

        return teamItems;
    }

    private getProjectDropdownItems(): IListBoxItem[] {
        return this.getTeamItems(this.props.projects ? this.props.projects : []);
    }

    private getTeamItems(groups: Group[]): IListBoxItem[] {
        return groups
            .map((group) => ({
                id: group.id,
                text: group.name
            }))
            .sort((groupA, groupB) => localeComparer(groupA.text, groupB.text));
    }

    private getTitleText(): string {
        if (typeof this.props.title === "function") {
            return this.props.title(this.state.fields.name);
        }

        return this.props.title;
    }

    private handleDismiss = () => {
        this.props.onDismiss();
    };

    private handleSubmit = () => {
        this.setState(
            {
                submitting: true,
                submissionError: null
            },
            async () => {
                try {
                    await this.props.onSubmit(this.state.fields, this.state.selectedDashboardType);
                } catch (error) {
                    this.setState({ submitting: false, submissionError: error.message });
                }
            }
        );
    };

    private loadTeams() {
        // The default dropdown choice
        const initialTeamId = this.getInitialTeamId();
        this.setState(
            {
                teamItems: this.getTeamDropdownItems(),
                teamsLoading: false,
                fields: {
                    ...this.state.fields,
                    teamId: initialTeamId || this.state.fields.teamId,
                },
                errors: {
                    ...this.state.errors,
                    teamId: initialTeamId ? null : this.state.errors.teamId
                },
            },
            () => {
                const initialTeamIndex = this.state.teamItems.findIndex((teamItem) => teamItem.id === initialTeamId);
                if (initialTeamIndex !== -1) {
                    this.teamSelection.select(initialTeamIndex);
                }
            }
        );
    }

    private loadQueryFolder() {
        const initialQueryId = this.getInitialQueryId();
        this.setState({
            fields: {
                ...this.state.fields,
                queryFolderPath: initialQueryId || this.state.fields.queryFolderPath
            }
        });
    }
    private loadProjectAndTeams() {
        // The default dropdown choices
        const initialProjectId = this.getInitialProjectId();
        const initialTeamId = this.getInitialTeamId();

        const teamitems = this.getTeamDropdownItems(true, initialProjectId);
        const projectItems = this.getProjectDropdownItems();
        let tempProjId = initialProjectId || this.state.fields.projectId;
        let tempFlag = tempProjId === this.props.dashboardProject.id;

        this.setState(
            {
                teamItems: teamitems ?? [],
                projectItems: projectItems ?? [],
                teamsLoading: !(teamitems && teamitems.length !== 0),
                projectsLoading: !(projectItems && projectItems.length !== 0),
                fields: {
                    ...this.state.fields,
                    teamId: initialTeamId || this.state.fields.teamId,
                    projectId: initialProjectId || this.state.fields.projectId,
                    copyQueriesFlag: tempFlag ? this.state.fields.copyQueriesFlag : true
                },
                errors: {
                    ...this.state.errors,
                    teamId: initialTeamId ? null : this.state.errors.teamId,
                    projectId: initialProjectId ? null : this.state.errors.projectId
                },
                disableQueryCheckboxfield: tempFlag ? false : true
            },
            () => {
                const initialTeamIndex = this.state.teamItems.findIndex((teamItem) => teamItem.id === initialTeamId);
                if (initialTeamIndex !== -1) {
                    this.teamSelection.select(initialTeamIndex);
                }

                const initialProjectIndex = this.state.projectItems.findIndex((projectItem) => projectItem.id === initialProjectId);
                if (initialProjectIndex !== -1) {
                    this.projectSelection.select(initialProjectIndex);
                }
            }
        );
    }

    private loadTeamsForSpecificProject() {
        if (this.state.fields.projectId) {
            let teamIndex = 0; // Setting explicity to the start of list in case of new project selection. There must be a default team
            let teamDropDownItems =
                this.state.selectedDashboardType === DashboardType.Team
                    ? this.getTeamDropdownItems(true, this.state.fields.projectId)
                    : this.state.teamItems;
            this.setState(
                {
                    teamItems: this.state.selectedDashboardType === DashboardType.Team ? teamDropDownItems : this.state.teamItems,
                    teamsLoading: false,
                    fields: {
                        ...this.state.fields,
                        teamId: teamDropDownItems[teamIndex].id,
                    },
                    errors: {
                        ...this.state.errors,
                        projectId: null,
                        teamId: null
                    },
                },
                () => {
                    this.teamSelection.select(teamIndex);
                }
            );
        }
    }

    private onNameChange = (event: React.ChangeEvent<HTMLInputElement>, newName: string) => {
        const newFields = {
            ...this.state.fields,
            name: newName
        };

        this.setState({
            fields: newFields,
            errors: {
                ...this.state.errors,
                ...validate(newFields, this.props.showOwnerField)
            }
        });
    };

    private onDescriptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>, newDescription: string) => {
        const newFields = {
            ...this.state.fields,
            description: newDescription
        };
        this.setState({
            fields: newFields,
            errors: {
                ...this.state.errors,
                ...validate(newFields, this.props.showOwnerField)
            }
        });
    };

    private onOwnerChange = (newOwner?: IIdentity): void => {
        if (newOwner) {
            const newFields = {
                ...this.state.fields,
                owner: newOwner
            };

            this.setState({
                fields: newFields,
                errors: {
                    ...this.state.errors,
                    ...validate(newFields, this.props.showOwnerField)
                }
            });
        }
    };

    private onOwnerClear = (): void => {
        const newFields = {
            ...this.state.fields,
            owner: undefined
        };

        this.setState({
            fields: newFields,
            errors: {
                ...this.state.errors,
                ...validate(newFields, this.props.showOwnerField)
            }
        });
    };

    private isFormInvalid(): boolean {
        const fieldNames = Object.keys(this.state.errors) as [keyof FormErrors];
        return (
            !this.areProjectTeamPopulatedForDashboardType() ||
            this.state.fields.name === "" ||
            fieldNames.some((fieldName) => this.state.errors[fieldName] !== null)
        );
    }

    private areProjectTeamPopulatedForDashboardType(): boolean {
        if (this.props.showDashboardProjectTeamSelector) {
            if (this.state.selectedDashboardType == DashboardType.Project) {
                return !!this.state.fields.projectId;
            } else if (this.state.selectedDashboardType == DashboardType.Team) {
                return !!this.state.fields.projectId && !!this.state.fields.teamId;
            }
            return false;
        }

        return true;
    }

    private isTeamDashboard() {
        return this.props.dashboard!.dashboardScope === DashboardScope.Project_Team;
    }

    private onAutoRefreshChange = (event: React.SyntheticEvent<HTMLElement>, newAutoRefresh: boolean) => {
        const newFields = {
            ...this.state.fields,
            autoRefresh: newAutoRefresh
        };
        this.setState({
            fields: newFields
        });
    };
    private onCopyQueriesFlagChange = (event: React.SyntheticEvent<HTMLElement>, newCopyQueriesFlag: boolean) => {
        const newFields = {
            ...this.state.fields,
            copyQueriesFlag: this.state.disableQueryCheckboxfield ? this.state.fields.copyQueriesFlag : newCopyQueriesFlag
        };
        this.setState({
            fields: newFields
        });
    };
    private onDashboardTypeSelect = (selectedId: DashboardType) => {
        let teamIdFormError: string | null = null;

        // If team type is selected, make sure the user selects a team as well.
        if (selectedId === DashboardType.Team && !this.state.fields.teamId) {
            teamIdFormError = Resources.ValueRequired;
        }

        this.setState({
            selectedDashboardType: selectedId,
            errors: {
                ...this.state.errors,
                teamId: teamIdFormError
            }
        });
    };

    private onDashboardProjectTeamSelect = (selectedId: DashboardType) => {
        let projectIdFormError: string | null = null;

        //Only for copy scenario
        //project should be selected, as we are either setting team value/keeping it the same
        projectIdFormError = !this.state.fields.projectId ? Resources.ValueRequired : null;
        let tempFlag = this.state.fields.projectId === this.props.dashboardProject?.id;

        if (selectedId === DashboardType.Project) {
            this.setState({
                selectedDashboardType: selectedId,
                fields: {
                    ...this.state.fields,
                    copyQueriesFlag: tempFlag ? this.state.fields.copyQueriesFlag : true
                },
                errors: {
                    ...this.state.errors,
                    projectId: projectIdFormError,
                    teamId: null
                },
                disableQueryCheckboxfield: tempFlag ? false : true
            });
        } else {
            let initialIndex = 0; // Setting explicity to the start of list.
            let teamDropDownItems = this.getTeamDropdownItems(true, this.state.fields.projectId);
            if (!this.props.projects || this.props.projects.length == 0) {
                this.setState({
                    ...this.state,
                    selectedDashboardType: selectedId,
                    teamItems: [],
                    teamsLoading: true,
                    submitting: false,
                    fields: {
                        ...this.state.fields,
                        teamId: undefined,
                        copyQueriesFlag: tempFlag ? this.state.fields.copyQueriesFlag : true
                    },
                    errors: {
                        ...this.state.errors,
                        projectId: null,
                        teamId: null
                    },
                    disableQueryCheckboxfield: tempFlag ? false : true
                });
            } else if (!teamDropDownItems || teamDropDownItems.length == 0) {
                const projectItem = this.props.projects.find((ele) => {
                    return ele.id == this.state.fields.projectId;
                })!;

                this.setState({
                    ...this.state,
                    selectedDashboardType: selectedId,
                    teamItems: [],
                    teamsLoading: true,
                    submitting: false,
                    fields: {
                        ...this.state.fields,
                        teamId: undefined,
                        copyQueriesFlag: tempFlag ? this.state.fields.copyQueriesFlag : true
                    },
                    errors: {
                        ...this.state.errors,
                        projectId: null,
                        teamId: null
                    },
                    disableQueryCheckboxfield: tempFlag ? false : true
                });

                this.props.onProjectSelectLoadTeam?.(projectItem);
            } else {
                this.setState(
                    {
                        selectedDashboardType: selectedId,
                        teamItems: teamDropDownItems,
                        fields: {
                            ...this.state.fields,
                            teamId: teamDropDownItems[initialIndex].id,
                            copyQueriesFlag: tempFlag ? this.state.fields.copyQueriesFlag : true
                        },
                        errors: {
                            ...this.state.errors,
                            projectId: projectIdFormError,
                            teamId: null
                        },
                        disableQueryCheckboxfield: tempFlag ? false : true
                    },
                    () => {
                        this.teamSelection.select(initialIndex);
                    }
                );
            }
        }
    };

    private onTeamSelect = (event: React.SyntheticEvent<HTMLElement>, newTeamItem: IListBoxItem) => {
        this.setState({
            fields: {
                ...this.state.fields,
                teamId: newTeamItem.id,
            },
            errors: {
                ...this.state.errors,
                teamId: null
            },
        });
    };

    private onProjectSelect = (event: React.SyntheticEvent<HTMLElement>, newProjectItem: IListBoxItem) => {
        if (this.props.showDashboardProjectTeamSelector === true && newProjectItem.id !== this.state.fields.projectId) {
            let teamIndex = 0; // Setting explicity to the start of list in case of new project selection. There must be a default team
            let teamDropDownItems =
                this.state.selectedDashboardType === DashboardType.Team ? this.getTeamDropdownItems(true, newProjectItem.id) : this.state.teamItems;
            let tempFlag = newProjectItem.id === this.props.dashboardProject.id;

            //If team dropdown doesn't have relevant records, fetch by setting loading, and triggering action to load.
            if (!teamDropDownItems || teamDropDownItems.length === 0) {
                this.setState({
                    teamItems: [],
                    teamsLoading: true,
                    submitting: false,
                    fields: {
                        ...this.state.fields,
                        projectId: newProjectItem.id,
                        teamId: undefined,
                        copyQueriesFlag: tempFlag ? this.state.fields.copyQueriesFlag : true
                    },
                    errors: {
                        ...this.state.errors,
                        projectId: null,
                        teamId: null
                    },
                    disableQueryCheckboxfield: tempFlag ? false : true
                });

                this.props.onProjectSelectLoadTeam?.(
                    this.props.projects?.find((ele) => {
                        return ele.id === newProjectItem.id;
                    }) ?? { id: newProjectItem.id, name: newProjectItem.text! }
                );
            }
            //Set state if list is already present in state.
            else {
                let tempFlag = newProjectItem.id === this.props.dashboardProject.id;
                this.setState(
                    {
                        teamItems: this.state.selectedDashboardType === DashboardType.Team ? teamDropDownItems : this.state.teamItems,
                        fields: {
                            ...this.state.fields,
                            projectId: newProjectItem.id,
                            teamId: teamDropDownItems[teamIndex].id,
                            copyQueriesFlag: tempFlag ? this.state.fields.copyQueriesFlag : true
                        },
                        errors: {
                            ...this.state.errors,
                            projectId: null,
                            teamId: null
                        },
                        disableQueryCheckboxfield: tempFlag ? false : true
                    },
                    () => {
                        this.teamSelection.select(teamIndex);
                    }
                );
            }
        }
    };

    private renderDashboardTypeRadioButton(dashboardType: DashboardType) {
        const labelId = "rb-label-" + dashboardType;
        const labelInfoId = "rb-label-info-" + dashboardType;
        const optionId = dashboardType === DashboardType.Team ? Resources.DashboardTypeTeamOption : Resources.DashboardTypeProjectOption;
        const typeDescription =
            dashboardType === DashboardType.Team ? Resources.DashboardTypeTeamDescription : Resources.DashboardTypeProjectDescription;

        return (
            <>
                <RadioButton id={dashboardType} ariaLabelledBy={labelId} ariaDescribedBy={labelInfoId}>
                    <div className="dashboard-panel-dashboard-type-radio-button">
                        <div className="bolt-radio-button-label" id={getSafeId(labelId)}>
                            {optionId}
                        </div>
                    </div>
                </RadioButton>
                <div className="dashboard-panel-dashboard-type-radio-button-sub-text font-size-ms secondary-text" id={getSafeId(labelInfoId)}>
                    {typeDescription}
                </div>
            </>
        );
    }

    private renderDashboardTypeSelector(): JSX.Element {
        if (isFeatureEnabled(this.context.pageContext, "ms.vss-dashboards.team-agnostic-dashboards-feature", false)) {
            return (
                <>
                    <RadioButtonGroup
                        className="dashboard-panel-dashboard-type-section"
                        onSelect={this.onDashboardTypeSelect}
                        selectedButtonId={this.state.selectedDashboardType}
                        text={Resources.DashboardTypeFieldLabel}
                    >
                        {this.renderDashboardTypeRadioButton(DashboardType.Team)}
                        {this.renderDashboardTypeRadioButton(DashboardType.Project)}
                    </RadioButtonGroup>
                    {this.state.selectedDashboardType === DashboardType.Team ? this.renderTeamDropdownField() : null}
                </>
            );
        }

        return this.renderTeamDropdownField();
    }

    private renderDashboardProjectTeamSelector(): JSX.Element {
        return (
            <>
                <RadioButtonGroup
                    className="dashboard-panel-dashboard-project-team-section"
                    onSelect={this.onDashboardProjectTeamSelect}
                    selectedButtonId={this.state.selectedDashboardType}
                    text={Resources.DashboardTypeFieldLabel}
                >
                    {this.renderDashboardTypeRadioButton(DashboardType.Team)}
                    {this.renderDashboardTypeRadioButton(DashboardType.Project)}
                </RadioButtonGroup>
                {this.renderProjectDropdownField()}
                {this.state.selectedDashboardType === DashboardType.Team ? this.renderTeamDropdownField() : null}
            </>
        );
    }

    private renderOwnerField(): JSX.Element | null {
        return (
            <FormItem
                className="dashboard-panel-owner-form-item"
                label={this.props.dashboard && this.isTeamDashboard() ? Resources.TeamDashboardOwnerFieldLabel : Resources.OwnerFieldLabel}
            >
                <IdentityPickerDropdown
                    disabled={this.props.disableOwnerField}
                    pickerProvider={this.pickerProvider}
                    onChange={this.onOwnerChange}
                    onClear={this.onOwnerClear}
                    suggestionsContainerAriaLabel={Resources.SelectOwnerAriaLabel}
                    value={this.state.fields.owner}
                    editPlaceholder={Resources.DashboardOwnerPicker_SearchUsers}
                />
            </FormItem>
        );
    }

    private renderSubmissionErrorMessage(): JSX.Element | null {
        if (this.state.submissionError) {
            announce(this.state.submissionError);
            return (
                <MessageCard onDismiss={() => this.setState({ submissionError: null })} severity={MessageCardSeverity.Error}>
                    {this.state.submissionError}
                </MessageCard>
            );
        }

        return null;
    }

    private renderTeamDropdownField(): JSX.Element {
        return (
            <FormItem className="dashboard-panel-team-form-item" label={Resources.TeamFieldLabel}>
                <Dropdown
                    items={this.state.teamItems}
                    loading={this.state.teamsLoading}
                    onSelect={this.onTeamSelect}
                    selection={this.teamSelection}
                    placeholder={this.state.teamsLoading ? Resources.TeamFieldDropdownLoadingPrompt : Resources.TeamFieldDropdownPrompt}
                    disabled={this.state.teamsLoading || this.state.submitting}
                />
            </FormItem>
        );
    }
    private onQueriesChanged = (newQuery: string) => {
        const newFields = {
            ...this.state.fields,
            queryFolderPath: newQuery
        };
        this.setState({
            fields: newFields
        });
    };

    private renderQueryFolderDropdownField(): JSX.Element {
        return (
            <FormItem className="padding-top-16" label={Resources.QueryFolderDropdownFieldLabel}>
                <WrappedComponent
                    dependencies={[" ms.vss-work-web.common-query-save-dialog"]}
                    wrappedType="FolderDropdownGeneral"
                    {...{
                        mode: 1,
                        onQueriesChanged: this.onQueriesChanged,
                        navigateToQueryOnSave: false,
                        projectId:this.state.fields.projectId
                    }}
                    disabled={this.state.submitting}
                ></WrappedComponent>
            </FormItem>
        );
    }
    private renderProjectDropdownField(): JSX.Element {
        return (
            <FormItem className="dashboard-panel-project-form-item" label={Resources.ProjectFieldLabel}>
                <Dropdown
                    items={this.state.projectItems}
                    loading={this.state.projectsLoading}
                    onSelect={this.onProjectSelect}
                    selection={this.projectSelection}
                    placeholder={this.state.projectsLoading ? Resources.ProjectFieldDropdownLoadingPrompt : Resources.ProjectFieldDropdownPrompt}
                    disabled={this.state.projectsLoading || this.state.submitting}
                />
            </FormItem>
        );
    }
}
